import { Link } from "react-router-dom";
import { useDraggable } from "@dnd-kit/core";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import UserAvatar from "@/components/common/UserAvatar";
import { PHASE_LABELS } from "@/utils/deals.utils";
import { absoluteUrl, cn } from "@/utils/common.utils";
import { dealHealth, dealProgress, formatCloseDate, formatMoney } from "@/utils/deals.utils";
import { DealType } from "@/enum/common.enum";
import type { Deal } from "@/types/deals.types";

interface DealCardFaceProps {
  deal: Deal;
  className?: string;
}

/**
 * The card itself, with no link or drag behaviour attached.
 *
 * Split out so the board's `DragOverlay` can render the exact same face under
 * the cursor while the real card sits dimmed in its column — the overlay lives
 * outside the column's DOM, so it can't reuse the interactive wrapper.
 */
export function DealCardFace({ deal, className }: DealCardFaceProps) {
  const healthKey = dealHealth(deal);
  const progress = dealProgress(deal);
  const price = formatMoney(deal.contract_price || deal.list_price);
  const closing = formatCloseDate(deal.contract_terms?.closing_date || deal.close_date);
  const signals = deal.health_signals || [];

  return (
    <Card className={cn("min-h-52 overflow-hidden border-dash-border shadow-none transition-all", className)}>
      {deal.hero_image_url && (
        // draggable={false}: the browser's native image drag would steal the
        // pointer from dnd-kit, the same reason the link sets it.
        <img src={absoluteUrl(deal.hero_image_url)} alt={`Photo of ${deal.address}`} loading="lazy" draggable={false} className="h-32 w-full object-cover" />
      )}
      <CardHeader className="gap-1.5 p-5 pb-3">
        <CardTitle className="min-w-0 break-words text-base font-semibold leading-snug text-dash-ink">{deal.address}</CardTitle>
        <p className="break-words text-sm text-dash-faint">{deal.client_name} · {deal.deal_type === DealType.BUY ? "Buyer" : "Seller"}</p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 px-5 pb-4">
        <div className="flex items-center gap-3">
          {/* Brief 1.13: no alarm colouring. A deal needing attention shows a
              plain signal sentence below, not a red bar. */}
          <Progress value={progress} className={cn("h-1.5 bg-dash-chip", healthKey === "on_track" ? "[&>div]:bg-dash-greenMid" : "[&>div]:bg-dash-slate")} />
          <span className="shrink-0 text-xs text-dash-faint">{progress}%</span>
        </div>
        <div className="flex min-h-5 flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-dash-faint">
          <span>{closing ? `Closes ${closing}` : PHASE_LABELS[deal.phase]}</span>
          {signals.length > 0 && <span className="text-dash-ink">{signals[0]}</span>}
        </div>
      </CardContent>
      <CardFooter className="flex-col items-stretch gap-3 border-t border-dash-border px-5 py-4">
        {price && <span className="break-words text-lg font-semibold text-dash-ink">{price}</span>}
        <div className="flex items-center gap-2">
          <UserAvatar name={deal.agent_name} src={deal.agent_photo_url} className="size-6 shrink-0" />
          <span className="break-words text-xs font-medium text-dash-faint">{deal.agent_name}</span>
        </div>
      </CardFooter>
    </Card>
  );
}

interface DealCardProps {
  deal: Deal;
  /** Internal users can drag a card between columns to change the deal's phase.
   *  Clients and external collaborators get the same card, link only. */
  draggable?: boolean;
}

export default function DealCard({ deal, draggable = false }: DealCardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: deal.id,
    data: { deal },
    disabled: !draggable,
    // Keep the link's own semantics. dnd-kit would otherwise stamp
    // `role="button"` over an anchor that still navigates on Enter.
    attributes: { role: "link", roleDescription: "deal card — press space to move it between columns" },
  });

  return (
    <Link
      ref={setNodeRef}
      to={`/deals/${deal.id}`}
      className={cn(
        "group block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dash-greenDeep focus-visible:ring-offset-2",
        // The DragOverlay renders the travelling copy, so the original just
        // leaves a ghost behind rather than disappearing and collapsing the column.
        isDragging && "opacity-40"
      )}
      // `manipulation` (not `none`) keeps the board scrollable on touch — the
      // TouchSensor's press-and-hold delay is what separates a drag from a scroll.
      style={draggable ? { touchAction: "manipulation" } : undefined}
      // An <a> is natively draggable: the browser's own link drag grabs the
      // pointer, dnd-kit sees `pointercancel`, and the card never moves.
      draggable={false}
      // …and so is an agent photo inside it; `dragstart` bubbles, so one handler
      // covers every child.
      onDragStart={(e) => e.preventDefault()}
      data-testid={`deal-card-${deal.id}`}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
    >
      <DealCardFace
        deal={deal}
        className="group-hover:-translate-y-0.5 group-hover:border-dash-greenDeep group-hover:shadow-md"
      />
    </Link>
  );
}
